import uniqid from "uniqid";

import contentTypes from "astrid-distribution/src/constants/contentTypes";
import { getDocumentData } from "astrid-firestore/src/helpers";
import arrayChunk from "astrid-helpers/src/arrayChunk";

import artifactValidationStatuses from "../../src/api/artifacts/constants/artifactValidationStatuses";
import productionStatuses from "../../src/api/productions/constants/productionStatuses";

import createDocumentIdAndRef from "./createDocumentIdAndRef";
import createMarkers from "./createMarkers";
import createStorageFile from "./createStorageFile";

export default async function createAudioArtifact(
	{ api, env },
	{ production, article, articleType = "total", isOnlyFetchingFiles = false },
) {
	if (api.withoutFiles && production.status === productionStatuses.DONE) {
		// used for batch migration. But should be ignored if production is ongoing.
		return null;
	}

	let filesToConnectToArtifact = null;

	if (production.master?.[articleType]?.files?.length > 0) {
		filesToConnectToArtifact = production.master[articleType].files;
	} else if (articleType === "total" && production.master?.proof?.files?.length > 0) {
		filesToConnectToArtifact = production.master.proof.files;
	} else if (articleType === "total" && production.master?.parking?.files?.length > 0) {
		filesToConnectToArtifact = production.master.parking.files;
	}

	if (!filesToConnectToArtifact?.length) {
		if (isOnlyFetchingFiles) {
			throw new Error("No files could be found!!");
		} else {
			return null; // do not create the artifact if we can not find files anywhere.
		}
	}
	const master = await getDocumentData(
		api[env].db.collection("productions").doc(production.id).collection("meta").doc("master"),
	);

	let artifactSize = 0;
	let artifactDuration = 0;
	let artifactFiles = [];

	for (const chunk of arrayChunk(filesToConnectToArtifact, 10)) {
		const files = await Promise.all(
			chunk
				.filter((fileId) => master[fileId])
				.map(async (fileId) => {
					const file = master[fileId];
					const fallbackBucket = env === "live" ? "earselect-static" : "stage-earselect-static";
					const bucketName = file.mastered?.bucket || file.original.bucket || fallbackBucket;
					const [{ updated, contentType, ...metadata }] = await api[env].getStorageMetadata(
						bucketName,
						file.mastered?.path || file.original.path,
					);

					return createStorageFile({
						file: {
							id: uniqid(),
							name: file.name,
							created: file.created || file.conversion || null,
							fullPath: file.mastered?.path || file.original.path,
							url: file.mastered?.url || file.original.url,
							size: Number(metadata.size),
							duration: file.duration,
						},
						metadata: {
							...metadata,
							contentType:
								contentType === "audio/wave" ||
								contentType === "audio/x-wav" ||
								(contentType === "application/octet-stream" && file.name.endsWith(".wav"))
									? "audio/wav"
									: contentType,
						},
					});
				}),
		);

		artifactSize += files.reduce((size, file) => size + Number(file.size), 0);
		artifactDuration += files.reduce((size, file) => size + Number(file.duration), 0);
		artifactFiles = artifactFiles.concat(files);
	}

	if (!artifactFiles.length) {
		return null;
	}

	const markers = await createMarkers({ api, env }, { production, articleType, artifactFiles });

	const { id, ref } = createDocumentIdAndRef({ api, env }, { collection: "artifacts" });
	const created =
		[...artifactFiles].sort((a, b) => b?.created?.toDate?.() - a?.created?.toDate?.())[0]?.created || null;

	return {
		id,
		ref,
		article:
			production.status === productionStatuses.DONE || api.isFinalizing ? (article?.isbn ? article : null) : null,
		created,
		updated: created,
		files: artifactFiles,
		markers: markers || [],
		name: article.name,
		size: artifactSize,
		duration: artifactDuration,
		status: artifactValidationStatuses.PENDING,
	};
}
